import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Tabs, Typography} from 'antd';

import PageTitle from '../../../components/page/PageTitle';
import SharesHistoryTable from '../../../components/shares/SharesHistoryTable';
import SharesOwned from '../../../components/shares/SharesOwned';
import {showMessages, messageType} from '../../../helpers/message';

import './Shares.less';
import {SharableType} from '../../../types/purchaseTypes';
import {useGetPurchasesQuery, useGetSharesQuery} from '../../../store/api/purchaseApi';
import {BaseError} from '../../../types/apiTypes';
import {useAppSelector} from '../../../hooks/useStore';
import {selectCurrentProfile, selectHasMemberRole} from '../../../store/slices/userSlice';
import {RootState} from '../../../store/store';

const {Text} = Typography;

// Location param refers to the url, components directly called from the buildRoutes file cannot have
// a param passed in. We use location to identify if it's the /shares_personal path or /shares_business path.
const Shares = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const sharableType: SharableType = location.pathname.includes('person') ? 'person' : 'company';

    const activeProfile = useAppSelector(selectCurrentProfile);
    const activeAccount = activeProfile?.account;

    // This hook acts as an onMount. If staff tries to access the submenu item before selecting a membership
    // redirect to select member page, before attempting to fire the API call which would throw an error.
    React.useEffect(() => {
        if (!activeProfile || !activeAccount) navigate('/member_change');
    }, [navigate, activeProfile, activeAccount]);

    let isOwner = useAppSelector((state: RootState) => selectHasMemberRole(state, ['user.owner']));

    // Disable F/E buttons on personal shares page if the active account is business.
    if (sharableType === 'person' && activeAccount?.is_business === true) isOwner = false;

    const sharableID = sharableType === 'person'
        ? activeProfile?.person?.id
        : activeAccount?.company_id;

    const {
        data: sharesData,
        error: sharesError,
        isLoading: sharesLoading
    } = useGetSharesQuery({sharableType, sharableID});
    const {
        data: purchasesData,
        error: purchasesError,
        isLoading: purchasesLoading
    } = useGetPurchasesQuery(
        {
            itemType: 'share',
            recipientType: sharableType,
            recipientID: sharableID
        }
    );

    React.useEffect(() => {
        if (sharesError || purchasesError) {
            showMessages((sharesError || purchasesError) as BaseError, messageType.error);
        }
    }, [sharesError, purchasesError]);

    const disclaimerText = (
        <Text className="shares-container-owner-disclaimer-text">
            {`You do not have permission to ${sharableType === 'company' ? 'view, ' : ''}buy or sell shares.
            Please contact the Owner of the account for more information.`}
        </Text>
    );

    const sharesComponents = (
        <>
            <SharesOwned
                loading={sharesLoading}
                numberOfShares={sharesData?.number ?? 0}
                totalValue={sharesData?.total ?? 0}
                shareOwner={sharesData?.owner ?? ''}
            />
            <SharesHistoryTable
                loading={purchasesLoading}
                totalRows={purchasesData?.length ?? 0}
                purchases={purchasesData || []}
                sharableType={sharableType}
                isOwner={isOwner}
            />
        </>
    );

    // Owner on business account: view personal shares w/ disclaimerText - view/purchase company shares.
    // Not owner on business account: view personal shares w/ disclaimerText - hide view/purchase company shares /w disclaimer text.
    // Owner on personal account: view/purchase personal shares.
    // Not owner on personal account: view personal shares w/ disclaimerText.
    const tabsChildren = !isOwner ? (sharableType === 'company' ? disclaimerText : <> {disclaimerText}{sharesComponents} </>)
        : sharesComponents;

    const tabsItems = [
        {
            label: 'General Information',
            key: '1',
            children: <>{tabsChildren}</>
        }
    ];

    return (
        <>
            <PageTitle title={sharableType === 'person' ? 'Personal Shares' : 'Business Shares'}/>
            <Tabs defaultActiveKey="1" items={tabsItems}/>
        </>
    );
};

export default Shares;
